import { useEffect, useState } from 'react'
import Logo from 'components/common/Logo'
import { useApp, useAuth, useTheme } from 'hooks'
import { LogOut, Moon, SquareArrowOutUpRight, Sun, User } from 'lucide-react'
import moment from 'moment'
import { Nav, Navbar, NavDropdown } from 'react-bootstrap'
import { NavLink } from 'react-router'
import QuickSuiteChatDrawer from 'ts/components/quick-suite-chat/QuickSuiteChatDrawer'
import './TopNav.scss'
import agentBillImg from '../../assets/images/agentbill.svg'

function TopNav() {
  const { user, clearUser } = useAuth()
  const { version, packagingType, lastRunTime, isQEnabled, repoFactory } = useApp()
  const { theme, setTheme } = useTheme()

  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatAgentId, setChatAgentId] = useState(null)
  const [isChatConfigLoaded, setIsChatConfigLoaded] = useState(false)

  // Resolve whether this user has any QuickSuite chat agent configured.
  // If not, we don't render the Ask Bill entry point or drawer at all.
  useEffect(() => {
    if (!isQEnabled || !repoFactory || !user?.authToken) {
      setChatAgentId(null)
      setIsChatConfigLoaded(true)
      return
    }

    let cancelled = false

    repoFactory.biRepo().getDashboardList(
      { token: user.authToken },
      {
        success: (response) => {
          if (cancelled) return
          try {
            const agentList = response?.getDashboardList?.data?.agentList || []
            const firstAgent = Array.isArray(agentList) && agentList.length > 0 ? agentList[0] : null
            setChatAgentId(firstAgent?.value || null)
          } catch (e) {
            setChatAgentId(null)
          } finally {
            setIsChatConfigLoaded(true)
          }
        },
        failure: () => {
          if (cancelled) return
          setChatAgentId(null)
          setIsChatConfigLoaded(true)
        }
      }
    )

    return () => {
      cancelled = true
    }
  }, [isQEnabled, repoFactory, user?.authToken])

  return (
    <>
      <Navbar sticky="top" className={`glass-nav py-2 ${theme === 'dark' ? 'shadow-lg' : 'shadow-sm'}`}>
        <div className="container-fluid px-5 justify-content-between">
          <Navbar.Brand>
            <NavLink to="/">
              <Logo alt="logo" height={40} />
            </NavLink>
          </Navbar.Brand>

          <div className="d-flex align-items-center">
            <Navbar.Collapse className="flex-grow-0">
              <Nav className="align-items-center">
                <Navbar.Text className="text-end ">
                  <strong className="fw-semibold text-nowrap">Welcome {user?.username}</strong>

                  <div className="text-sm text-nowrap">
                    {lastRunTime && `AWS spend as of ${moment.utc(lastRunTime).local().format('MM/DD/yyyy hh:mm A')}`}
                  </div>
                </Navbar.Text>
                <NavDropdown
                  title={
                    <div className="avatar shadow-sm">
                      <User size={20} />
                    </div>
                  }
                  className="user-dropdown nav-avatar-item"
                >
                  <NavDropdown.Item
                    as="a"
                    href="https://docs.finopscenter.com/personas-roles-activities"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SquareArrowOutUpRight size={16} className="me-2" />
                    Documentation
                  </NavDropdown.Item>

                  <NavDropdown.Item onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
                    {theme === 'dark' ? (
                      <div>
                        <Sun size={16} className="me-2" />
                        Light mode
                      </div>
                    ) : (
                      <div>
                        <Moon size={16} className="me-2" />
                        Dark mode
                      </div>
                    )}
                  </NavDropdown.Item>

                  <NavDropdown.Item onClick={clearUser}>
                    <LogOut size={16} className="me-2" /> Log out
                  </NavDropdown.Item>

                  <NavDropdown.Divider />

                  <NavDropdown.ItemText>
                    <div>Release v{version}</div>
                    <div>Version: {packagingType}</div>
                  </NavDropdown.ItemText>
                </NavDropdown>
              </Nav>
            </Navbar.Collapse>

            {isQEnabled && isChatConfigLoaded && chatAgentId && (
              <>
                <div className="vr align-self-center mx-3" style={{ height: '24px' }} />
                <Nav.Link onClick={() => setIsChatOpen(true)} className="ask-bill">
                  <img src={agentBillImg} alt="agent bill" height={46} />
                  <div className="text-nowrap">
                    Ask <span className="ask-bill--badge">Bill</span>
                  </div>
                </Nav.Link>
              </>
            )}
          </div>
        </div>
      </Navbar>
      {chatAgentId && (
        <QuickSuiteChatDrawer isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} agentId={chatAgentId} />
      )}
    </>
  )
}
export default TopNav
